(() => {
  if (window.__preorderPopupInit) return;
  window.__preorderPopupInit = true;

  const ADD_URL = /\/cart\/add(\.js|\.json)?(\?|$)/i;
  const CONTEXT_TTL = 8000;

  const baseFetch =
    typeof window.fetch === "function" ? window.fetch.bind(window) : null;
  const t = window.preorderPopupData?.strings ?? {};

  const confirmed = new Map();
  const remember = (id) => id != null && confirmed.set(String(id), Date.now());
  const isFresh = (id) => Date.now() - (confirmed.get(String(id)) ?? 0) < CONTEXT_TTL;

  const deferred = () => {
    if (typeof Promise.withResolvers === "function") {
      return Promise.withResolvers();
    }
    let resolve;
    const promise = new Promise((res) => {
      resolve = res;
    });
    return { promise, resolve };
  };

  const escapeHtml = (value) =>
    String(value).replace(
      /[&<>"']/g,
      (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[char])
    );

  const usDate = (iso) => {
    const [y, m, d] = String(iso).split("-");
    return y && m && d ? `${m}/${d}/${y}` : String(iso);
  };

  const FALLBACKS = {
    preorder: (when) =>
      `This item is a pre-order and is estimated to ship on ${when}.`,
    backorder: (when, minDate, maxDate) =>
      `This item is backordered until ${when} and is estimated to ship between ${minDate} - ${maxDate}.`,
    backorder_no_range: (when) => `This item is backordered until ${when}.`,
    made_to_order: () =>
      "This item is made to order and is not eligible for return.",
    returns: () => "This item is final sale.",
  };

  const describe = ({ type, date, range, title }) => {
    const key = type === "backorder" && !range ? "backorder_no_range" : type;
    const template = t[key];
    const when = date ? usDate(date) : "";
    const [minDate = "", maxDate = minDate] = (range ?? "").split(" - ");
    const body = template
      ? template
          .replaceAll("[date]", when)
          .replaceAll("[min_date]", minDate)
          .replaceAll("[max_date]", maxDate)
      : FALLBACKS[key](when, minDate, maxDate);

    return title ? `<strong>${escapeHtml(title)}</strong> &mdash; ${body}` : body;
  };

  const dateFor = (attr, id) => {
    for (const token of (attr ?? "").split(",")) {
      const [variant, date] = token.split(":");
      if (variant && date && Number(variant) === Number(id)) return date.trim();
    }
    return null;
  };

  const listsVariant = (attr, id) =>
    (attr ?? "").split(",").some((token) => token && Number(token) === Number(id));

  const entriesFrom = (source, title) => {
    const entries = [];
    if (source.preorder) entries.push({ type: "preorder", date: source.preorder, title });
    if (source.backorder) {
      entries.push({ type: "backorder", date: source.backorder, range: source.range, title });
    }
    if (source.made_to_order) entries.push({ type: "made_to_order", title });
    if (source.returns) entries.push({ type: "returns", title });
    return entries;
  };

  const domInfo = (id) => {
    for (const el of document.querySelectorAll(
      "[data-variants-backorder],[data-variants-preorder],[data-variant-ids]"
    )) {
      const preorder = dateFor(el.dataset.variantsPreorder, id);
      const backorder = dateFor(el.dataset.variantsBackorder, id);
      if (preorder || backorder || listsVariant(el.dataset.variantIds, id)) {
        return entriesFrom(
          {
            preorder,
            backorder,
            range: dateFor(el.dataset.variantsShippingRange, id),
            made_to_order: el.dataset.madeToOrder === "true",
            returns: el.dataset.return === "true",
          },
          el.dataset.productTitle
        );
      }
    }
    return null;
  };

  const variantCache = new Map();
  const inflight = new Map();

  const fetchProduct = (handle) => {
    if (!handle || !baseFetch) return Promise.resolve(null);
    if (variantCache.has(handle)) return Promise.resolve(variantCache.get(handle));
    if (inflight.has(handle)) return inflight.get(handle);

    const request = baseFetch(
      `/products/${encodeURIComponent(handle)}?view=backorder`,
      { credentials: "same-origin" }
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.variants) variantCache.set(handle, data);
        return data ?? null;
      })
      .catch(() => null)
      .finally(() => inflight.delete(handle));

    inflight.set(handle, request);
    return request;
  };

  const handleInUrl = (url) => url?.match(/\/products\/([^/?#]+)/)?.[1] ?? null;
  const pageHandle = () => handleInUrl(location.pathname);

  const handleNear = (el) => {
    if (!el?.closest) return pageHandle();

    const tagged = el.closest("[data-product-handle]");
    if (tagged) return tagged.dataset.productHandle;

    const card = el.closest(".product-card,.product-block,[data-product-block]");
    const href = card
      ?.querySelector('a[href*="/products/"]')
      ?.getAttribute("href");

    return handleInUrl(href) ?? pageHandle();
  };

  let context = { handle: null, at: 0 };
  const trackContext = ({ target }) => {
    const handle = handleNear(target);
    if (handle) context = { handle, at: Date.now() };
  };

  document.addEventListener("pointerdown", trackContext, true);
  document.addEventListener("click", trackContext, true);

  const currentHandle = () =>
    context.handle && Date.now() - context.at <= CONTEXT_TTL
      ? context.handle
      : pageHandle();

  const idsFromJson = (json) => {
    if (!json) return [];
    const ids = [];
    if (json.id != null) ids.push(String(json.id));
    for (const item of json.items ?? []) {
      if (item?.id != null) ids.push(String(item.id));
    }
    return ids;
  };

  const idsFromBody = (body) => {
    if (!body) return [];

    if (body instanceof FormData) {
      const ids = [];
      const single = body.get("id");
      if (single) ids.push(String(single));
      for (const [key, value] of body) {
        if (value && /^items\[\d+]\[id]$/.test(key)) ids.push(String(value));
      }
      return ids;
    }

    if (typeof body === "string") {
      try {
        return idsFromJson(JSON.parse(body));
      } catch {
        const single = new URLSearchParams(body).get("id");
        return single ? [String(single)] : [];
      }
    }

    return typeof body === "object" ? idsFromJson(body) : [];
  };

  const endpointInfo = async (ids) => {
    const data = await fetchProduct(currentHandle());
    if (!data?.variants) return [];

    const info = [];
    for (const id of ids) {
      const variant = data.variants[id];
      if (!variant) continue;
      const range = variant.shipping?.["Ships between"] ?? null;
      info.push(...entriesFrom({ ...variant, range }, data.product_title));
    }
    return info;
  };

  const infoForBody = async (body) => {
    const ids = [...new Set(idsFromBody(body))].filter((id) => !isFresh(id));
    if (!ids.length) return [];

    const info = [];
    const unresolved = [];
    for (const id of ids) {
      const found = domInfo(id);
      if (found === null) unresolved.push(id);
      else info.push(...found);
    }

    if (unresolved.length) info.push(...(await endpointInfo(unresolved)));

    const seen = new Set();
    return info.filter((entry) => {
      const key = `${entry.type}|${entry.date ?? ""}|${entry.range ?? ""}|${entry.title ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const confirmDialog = (info) => {
    const { promise, resolve } = deferred();
    const lastFocus = document.activeElement;

    const overlay = document.createElement("div");
    overlay.className = "preorder-popup";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.innerHTML = `
      <div class="preorder-popup__dialog" role="document">
        <h2 class="preorder-popup__title">${t.title ?? "Please confirm your order"}</h2>
        ${t.intro ? `<p class="preorder-popup__intro">${t.intro}</p>` : ""}
        <ul class="preorder-popup__list">
          ${info.map((entry) => `<li class="preorder-popup__item">${describe(entry)}</li>`).join("")}
        </ul>
        <div class="preorder-popup__actions">
          <button type="button" class="btn btn--secondary preorder-popup__btn preorder-popup__btn--cancel">${t.cancel ?? "Cancel"}</button>
          <button type="button" class="btn preorder-popup__btn preorder-popup__btn--confirm">${t.confirm ?? "Add to cart"}</button>
        </div>
      </div>`;

    let done = false;
    const settle = (result) => {
      if (done) return;
      done = true;
      overlay.classList.remove("is-open");
      document.removeEventListener("keydown", onKey, true);
      setTimeout(() => {
        overlay.remove();
        lastFocus?.focus?.();
      }, 180);
      resolve(result);
    };

    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        settle(false);
      }
    };

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) settle(false);
    });
    overlay.querySelector(".preorder-popup__btn--cancel").onclick = () => settle(false);
    overlay.querySelector(".preorder-popup__btn--confirm").onclick = () => settle(true);
    document.addEventListener("keydown", onKey, true);

    document.body.append(overlay);
    void overlay.offsetWidth;
    overlay.classList.add("is-open");
    overlay.querySelector(".preorder-popup__btn--confirm").focus();

    return promise;
  };

  const askToConfirm = (info) => {
    try {
      return confirmDialog(info);
    } catch (error) {
      console.debug("[preorder-popup] dialog failed", error);
      return Promise.resolve(true);
    }
  };

  const resubmit = (form, id) => {
    remember(id);
    form.dataset.preorderConfirmed = id;
    if (form.requestSubmit) form.requestSubmit();
    else form.submit();
  };

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!form?.matches?.("form.js-product-form")) return;

      const id = form.querySelector('[name="id"]')?.value;
      if (!id) return;

      if (form.dataset.preorderConfirmed === id) {
        delete form.dataset.preorderConfirmed;
        remember(id);
        return;
      }

      const info = domInfo(id);
      if (!info?.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      askToConfirm(info).then((ok) => {
        if (ok) resubmit(form, id);
      });
    },
    true
  );

  if (!baseFetch) return;

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === "string" ? input : input?.url;
    if (typeof url !== "string" || !ADD_URL.test(url)) {
      return nativeFetch.apply(this, arguments);
    }

    const self = this;
    const args = arguments;
    return (async () => {
      const info = await infoForBody(init?.body).catch(() => []);
      if (info.length && !(await askToConfirm(info))) {
        throw new DOMException("Add to cart cancelled by customer", "AbortError");
      }
      return nativeFetch.apply(self, args);
    })();
  };
})();
